import React from "react";
import { Link } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { ArrowRight, ChevronLeft, ChevronRight, Play, Volume2, VolumeX, Instagram } from "lucide-react";
import { fileUrl, youtubeEmbedUrl } from "@/lib/api";
import { DEFAULT_INSTAGRAM } from "@/lib/site";

/* ---------- shared ---------- */
function SmartLink({ to, className, children, ...rest }) {
  if (!to) return null;
  if (/^https?:\/\//i.test(to)) {
    return <a href={to} target="_blank" rel="noreferrer" className={className} {...rest}>{children}</a>;
  }
  return <Link to={to} className={className} {...rest}>{children}</Link>;
}

function useInView(ref, rootMargin = "200px") {
  const [seen, setSeen] = React.useState(false);
  React.useEffect(() => {
    const el = ref.current;
    if (!el || seen) return;
    if (typeof IntersectionObserver === "undefined") { setSeen(true); return; }
    const io = new IntersectionObserver(([e]) => { if (e.isIntersecting) { setSeen(true); io.disconnect(); } }, { rootMargin });
    io.observe(el);
    return () => io.disconnect();
  }, [ref, rootMargin, seen]);
  return seen;
}

function postToPlayer(iframe, func, args = []) {
  try {
    iframe?.contentWindow?.postMessage(JSON.stringify({ event: "command", func, args }), "*");
  } catch { /* ignore */ }
}

/** A YouTube iframe that always fills its box like `object-fit: cover`. */
function YouTubeCover({ videoId, onReady, frameRef, title = "banner-video" }) {
  const boxRef = React.useRef(null);
  const [size, setSize] = React.useState({ w: 0, h: 0 });
  const [visible, setVisible] = React.useState(false);

  React.useLayoutEffect(() => {
    const el = boxRef.current;
    if (!el) return;
    const measure = () => {
      const { width, height } = el.getBoundingClientRect();
      const ratio = 16 / 9;
      if (width / height > ratio) setSize({ w: width, h: width / ratio });
      else setSize({ w: height * ratio, h: height });
    };
    measure();
    const ro = typeof ResizeObserver !== "undefined" ? new ResizeObserver(measure) : null;
    ro?.observe(el);
    window.addEventListener("resize", measure);
    return () => { ro?.disconnect(); window.removeEventListener("resize", measure); };
  }, []);

  return (
    <div ref={boxRef} className="absolute inset-0 overflow-hidden pointer-events-none">
      {size.w > 0 && (
        <iframe
          ref={frameRef}
          title={title}
          src={youtubeEmbedUrl(videoId)}
          allow="autoplay; encrypted-media; picture-in-picture"
          onLoad={() => setTimeout(() => { setVisible(true); onReady?.(); }, 900)}
          className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 border-0 transition-opacity duration-700"
          style={{ width: size.w + 2, height: size.h + 2, opacity: visible ? 1 : 0 }}
        />
      )}
    </div>
  );
}

/* ---------- HERO: up to 3 sliding banners (image or YouTube) ---------- */
export function HeroSlider({ banners, loading }) {
  const slides = React.useMemo(() => banners || [], [banners]);
  const [idx, setIdx] = React.useState(0);
  const touchX = React.useRef(null);

  React.useEffect(() => { if (idx >= slides.length) setIdx(0); }, [slides.length, idx]);

  React.useEffect(() => {
    // Auto-scroll: always moves to the next banner (also while the mouse is over it)
    if (slides.length < 2) return;
    const ms = Math.max(3, Number(slides[idx]?.duration) || 5) * 1000;
    const t = setTimeout(() => setIdx((i) => (i + 1) % slides.length), ms);
    return () => clearTimeout(t);
  }, [idx, slides]);

  const go = (d) => setIdx((i) => (i + d + slides.length) % slides.length);

  if (loading) {
    return <section className="relative h-[85vh] min-h-[600px] bg-neutral-900 animate-pulse" data-testid="hero-loading"/>;
  }
  if (!slides.length) return null;

  const s = slides[Math.min(idx, slides.length - 1)];
  const isVideo = s.media_type === "youtube" && s.youtube_id;
  const hasText = !!(s.overline || s.title || s.subtitle || (s.cta_text && s.cta_link) || (s.cta2_text && s.cta2_link));

  return (
    <section
      className="relative h-[85vh] min-h-[600px] overflow-hidden bg-neutral-900"
      data-testid="hero-slider"
      onTouchStart={(e) => { touchX.current = e.touches[0].clientX; }}
      onTouchEnd={(e) => {
        if (touchX.current == null || slides.length < 2) return;
        const dx = e.changedTouches[0].clientX - touchX.current;
        if (Math.abs(dx) > 50) go(dx < 0 ? 1 : -1);
        touchX.current = null;
      }}
    >
      <AnimatePresence initial={false}>
        <motion.div
          key={s.id}
          className="absolute inset-0"
          initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
          transition={{ duration: 0.9, ease: "easeInOut" }}
        >
          {s.image_url && (
            <motion.img
              initial={{ scale: 1.08 }} animate={{ scale: 1 }} transition={{ duration: 8, ease: "easeOut" }}
              src={fileUrl(s.image_url)} alt={s.title || "banner"}
              className={`absolute inset-0 w-full h-full object-cover ${hasText ? "opacity-70" : "opacity-100"}`}
            />
          )}
          {isVideo && <YouTubeCover videoId={s.youtube_id}/>}
          {hasText
            ? <div className="absolute inset-0 bg-gradient-to-r from-black/60 via-black/30 to-transparent"/>
            : <div className="absolute inset-0 bg-black/10"/>}

          <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-8 lg:px-12 h-full flex flex-col justify-center">
            <motion.div
              initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.35, duration: 0.8 }}
              className="max-w-xl text-white"
            >
              {s.overline && <div className="text-xs tracking-[0.4em] uppercase mb-4 opacity-90 border-l-2 border-[#D4AF37] pl-3">{s.overline}</div>}
              {s.title && <h1 className="font-serif text-5xl sm:text-6xl lg:text-7xl font-medium leading-[1.05] mb-6 whitespace-pre-line">{s.title}</h1>}
              {s.subtitle && <p className="text-base sm:text-lg opacity-90 mb-8 max-w-lg leading-relaxed">{s.subtitle}</p>}
              {(s.cta_text || s.cta2_text) && (
                <div className="flex flex-wrap gap-3">
                  {s.cta_text && s.cta_link && (
                    <SmartLink to={s.cta_link} className="btn-silk rounded-[10px]" data-testid="hero-cta-primary">{s.cta_text} <ArrowRight size={14} className="ml-2"/></SmartLink>
                  )}
                  {s.cta2_text && s.cta2_link && (
                    <SmartLink to={s.cta2_link} className="inline-flex items-center justify-center rounded-[10px] border border-white bg-white/10 backdrop-blur text-white hover:bg-white hover:text-[#0E4D34] transition-all duration-200 text-xs font-semibold tracking-[0.15em] uppercase px-6 py-3.5" data-testid="hero-cta-secondary">{s.cta2_text}</SmartLink>
                  )}
                </div>
              )}
            </motion.div>
          </div>
        </motion.div>
      </AnimatePresence>

      {slides.length > 1 && (
        <>
          <button onClick={() => go(-1)} aria-label="Previous banner" data-testid="hero-prev"
                  className="hidden sm:flex absolute bottom-6 right-[4.75rem] lg:right-[5.5rem] z-20 w-11 h-11 rounded-full items-center justify-center border border-white/50 bg-black/20 backdrop-blur text-white hover:bg-white hover:text-[#0E4D34] transition-colors">
            <ChevronLeft size={18}/>
          </button>
          <button onClick={() => go(1)} aria-label="Next banner" data-testid="hero-next"
                  className="hidden sm:flex absolute bottom-6 right-6 lg:right-10 z-20 w-11 h-11 rounded-full items-center justify-center border border-white/50 bg-black/20 backdrop-blur text-white hover:bg-white hover:text-[#0E4D34] transition-colors">
            <ChevronRight size={18}/>
          </button>
          <div className="absolute bottom-8 left-1/2 -translate-x-1/2 z-20 flex gap-3">
            {slides.map((b, i) => (
              <button key={b.id} onClick={() => setIdx(i)} aria-label={`Go to banner ${i + 1}`} data-testid={`hero-dot-${i}`}
                      className="relative h-[3px] w-10 sm:w-14 bg-white/30 overflow-hidden">
                {i === idx && (
                  <motion.span
                    key={`${b.id}-${idx}`}
                    className="absolute inset-y-0 left-0 bg-white"
                    initial={{ width: "0%" }}
                    animate={{ width: "100%" }}
                    transition={{ duration: Math.max(3, Number(b.duration) || 5), ease: "linear" }}
                  />
                )}
              </button>
            ))}
          </div>
        </>
      )}
    </section>
  );
}

/* ---------- CRAFTSMANSHIP: YouTube videos that play continuously ---------- */
function CraftVideoPlayer({ video }) {
  const boxRef = React.useRef(null);
  const frameRef = React.useRef(null);
  const inView = useInView(boxRef);
  const [ready, setReady] = React.useState(false);
  const [muted, setMuted] = React.useState(true);

  const toggleSound = () => {
    const f = frameRef.current;
    if (muted) { postToPlayer(f, "unMute"); postToPlayer(f, "setVolume", [80]); postToPlayer(f, "playVideo"); }
    else postToPlayer(f, "mute");
    setMuted(!muted);
  };

  return (
    <div ref={boxRef} className="aspect-video bg-neutral-900 relative overflow-hidden" data-testid={`craft-video-${video.id}`}>
      {video.poster_url && (
        <img src={fileUrl(video.poster_url)} alt={video.title}
             className={`absolute inset-0 w-full h-full object-cover transition-opacity duration-700 ${ready ? "opacity-0" : "opacity-100"}`}/>
      )}
      {video.youtube_id && inView && (
        <iframe
          ref={frameRef}
          title={video.title || "Craftsmanship"}
          src={youtubeEmbedUrl(video.youtube_id)}
          allow="autoplay; encrypted-media; picture-in-picture"
          onLoad={() => setTimeout(() => setReady(true), 900)}
          className="absolute inset-0 w-full h-full border-0 pointer-events-none"
          style={{ transform: "scale(1.02)" }}
        />
      )}
      {video.youtube_id && (
        <button onClick={toggleSound} data-testid="craft-sound-toggle"
                aria-label={muted ? "Turn sound on" : "Mute"}
                className="absolute bottom-4 right-4 z-10 flex items-center gap-2 bg-black/55 hover:bg-black/75 text-white text-[10px] uppercase tracking-[0.2em] px-3 py-2 backdrop-blur">
          {muted ? <VolumeX size={14}/> : <Volume2 size={14}/>}{muted ? "Sound off" : "Sound on"}
        </button>
      )}
    </div>
  );
}

export function CraftVideos({ videos }) {
  if (!videos?.length) return null;
  return (
    <>
      {videos.map((v, i) => (
        <section key={v.id} className="max-w-[1400px] mx-auto px-4 sm:px-8 lg:px-12 py-16 sm:py-24" data-testid="craft-section">
          <div className="grid lg:grid-cols-12 gap-10 lg:gap-14 items-center">
            <div className={`lg:col-span-5 ${i % 2 ? "lg:order-2" : ""}`}>
              {v.overline && <div className="text-xs sm:text-sm font-bold uppercase tracking-[0.35em] text-[#0E4D34] mb-4">{v.overline}</div>}
              <h2 className="font-medium tracking-tight text-neutral-900 leading-[1.05] mb-6 text-4xl sm:text-5xl lg:text-6xl">{v.title}</h2>
              {v.description && <p className="text-neutral-600 mb-8 leading-relaxed text-base sm:text-lg lg:text-xl whitespace-pre-line">{v.description}</p>}
              {v.button_text && v.button_link && (
                <SmartLink to={v.button_link} className="btn-outline-silk rounded-[10px] !px-7 !py-3.5"><Play size={13} className="mr-2"/> {v.button_text}</SmartLink>
              )}
            </div>
            <div className="lg:col-span-7 rounded-2xl overflow-hidden shadow-[0_30px_70px_-30px_rgba(0,0,0,.55)]">
              <CraftVideoPlayer video={v}/>
            </div>
          </div>
        </section>
      ))}
    </>
  );
}

/* ---------- REELS: YouTube Shorts playing on loop + link to Instagram ---------- */
export const reelLink = (reel) => reel?.link || (reel?.youtube_id ? `https://www.youtube.com/shorts/${reel.youtube_id}` : "#");

function ReelCard({ reel, index }) {
  const boxRef = React.useRef(null);
  const frameRef = React.useRef(null);
  const inView = useInView(boxRef, "300px");
  const [ready, setReady] = React.useState(false);
  const [muted, setMuted] = React.useState(true);
  const toggleSound = (e) => {
    // the sound button must not open the reel link
    e.preventDefault();
    e.stopPropagation();
    const f = frameRef.current;
    if (muted) { postToPlayer(f, "unMute"); postToPlayer(f, "setVolume", [80]); postToPlayer(f, "playVideo"); }
    else postToPlayer(f, "mute");
    setMuted(!muted);
  };
  return (
    <a ref={boxRef} href={reelLink(reel)} target="_blank" rel="noreferrer"
       title={reel.caption || "Watch this reel"}
       className="group relative block aspect-[9/16] rounded-2xl overflow-hidden bg-neutral-900 shadow-[0_14px_40px_-18px_rgba(0,0,0,.45)] hover:shadow-[0_22px_50px_-16px_rgba(14,77,52,.55)] hover:-translate-y-1 transition-all duration-300 snap-start shrink-0 w-[68%] sm:w-[44%] lg:w-auto"
       data-testid={`reel-${index}`}>
      <img src={`https://i.ytimg.com/vi/${reel.youtube_id}/hqdefault.jpg`} alt="" aria-hidden="true"
           className={`absolute inset-0 w-full h-full object-cover scale-[1.35] transition-opacity duration-700 ${ready ? "opacity-0" : "opacity-100"}`}/>
      {/* fills the 9:16 frame: Shorts fit exactly, normal videos are centre-cropped */}
      {inView && <YouTubeCover videoId={reel.youtube_id} frameRef={frameRef} title={reel.caption || `Reel ${index + 1}`} onReady={() => setReady(true)}/>}
      <div className="absolute inset-x-0 bottom-0 h-28 bg-gradient-to-t from-black/70 to-transparent pointer-events-none"/>
      <div className="absolute top-3 left-3 inline-flex items-center gap-1.5 rounded-full bg-white/90 text-[#0E4D34] text-[10px] font-bold uppercase tracking-[0.15em] px-2.5 py-1 opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none">
        <Play size={10} fill="currentColor"/>Watch reel
      </div>
      {reel.caption && <div className="absolute left-3 right-12 bottom-3 text-white text-sm font-medium leading-snug line-clamp-2 pointer-events-none">{reel.caption}</div>}
      <button type="button" onClick={toggleSound} aria-label={muted ? "Turn sound on" : "Mute"} data-testid={`reel-sound-${index}`}
              className="absolute bottom-3 right-3 w-9 h-9 rounded-full bg-black/45 hover:bg-black/70 backdrop-blur text-white flex items-center justify-center">
        {muted ? <VolumeX size={15}/> : <Volume2 size={15}/>}
      </button>
    </a>
  );
}

const instagramHandle = (url) => {
  const m = (url || "").match(/instagram\.com\/([A-Za-z0-9._]+)/i);
  return m ? `@${m[1]}` : "";
};

export function ReelsSection({ reels, section, instagramUrl: igUrl }) {
  const instagramUrl = igUrl || DEFAULT_INSTAGRAM;
  const list = (reels || []).slice(0, 4);
  if (!list.length || section?.enabled === false) return null;
  const title = section?.title || instagramHandle(instagramUrl) || "Reels";
  return (
    <section className="py-16 sm:py-20" data-testid="reels-section">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-10">
        <div className="text-center mb-10">
          <div className="inline-flex items-center gap-2 text-xs font-bold uppercase tracking-[0.3em] text-[#0E4D34] mb-3"><Instagram size={14}/>{section?.overline || "Follow us"}</div>
          <h2 className="font-medium tracking-tight text-[#0E4D34] leading-tight" style={{ fontSize: "clamp(1.5rem, 6.4vw, 3.75rem)", overflowWrap: "anywhere" }}>{title}</h2>
          <p className="text-neutral-600 mt-3">{section?.subtitle || "Tap a reel to watch it in full."}</p>
        </div>
        {/* phones/tablets: swipe sideways · desktop: 4 in a row */}
        <div className="flex lg:grid lg:grid-cols-4 gap-4 lg:gap-6 overflow-x-auto lg:overflow-visible snap-x snap-mandatory no-scrollbar -mx-4 px-4 sm:mx-0 sm:px-0 pb-2">
          {list.map((r, i) => <ReelCard key={r.id} reel={r} index={i}/>)}
        </div>
        {instagramUrl && (
          <div className="text-center mt-10 sm:mt-12">
            <a href={instagramUrl} target="_blank" rel="noreferrer" data-testid="reels-instagram"
               className="inline-flex items-center gap-3 rounded-[10px] bg-[#0E4D34] hover:bg-[#0A3926] text-white px-8 py-4 text-xs font-semibold uppercase tracking-[0.2em] transition-colors">
              <Instagram size={16}/>{section?.button_text || "More reels on Instagram"}
            </a>
          </div>
        )}
      </div>
    </section>
  );
}
